import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { labsWrite } from '../lib/labs'

// ── useEpisodeRatings ─────────────────────────────────────────────────────────
// The user's own per-episode ratings for one show (reel scale 0.5–10, half
// steps). Replaces the old emoji reactions. The SHOW's overall rating stays
// separate and manual (wl.rate); the episode AVERAGE returned here is a
// SECONDARY stat only and must never overwrite the show rating.
// Keyed map: `${season}-${episode}` -> rating.

// Standalone one-off rate — write (or clear) a single episode rating without
// subscribing to the whole show's ratings. Used by the global RatePrompt and
// QuickMarkButton so a My List row doesn't have to mount the full hook (and
// fire a query) just to let you rate the episode you just marked.
export async function rateEpisode(showId, userId, season, episode, rating) {
  if (!userId || !showId) return
  const clearing = rating == null || rating === 0
  if (clearing) {
    const { error } = await labsWrite('clear episode rating', () => supabase.from('episode_ratings').delete()
      .match({ user_id: userId, show_id: Number(showId), season, episode }))
    if (error) { console.error('[ep rating delete]', error.message); window.dispatchEvent(new CustomEvent('nextep:save-failed', { detail: { action: 'clear episode rating' } })) }
    else broadcastRating(showId, season, episode, null)
    return
  }
  const { error } = await labsWrite('save episode rating', () => supabase.from('episode_ratings').upsert(
    { user_id: userId, show_id: Number(showId), season, episode, rating },
    { onConflict: 'user_id,show_id,season,episode' }
  ))
  if (error) { console.error('[ep rating save]', error.message); window.dispatchEvent(new CustomEvent('nextep:save-failed', { detail: { action: 'save episode rating' } })) }
  else broadcastRating(showId, season, episode, rating)
}

// The show page mounts ONE useEpisodeRatings instance and reads it for both the
// episode list AND the episode sheet — but this standalone rateEpisode() (the deck's
// hold-to-rate and Episode Pulse's post-mark peek) writes straight to Supabase with no
// way to tell that instance a rating just landed. Found 2026-09-17 walking the app as a
// fresh user: rate a cascade in the Pulse sheet, open one of those same episodes in the
// SAME session — still says "Tap a reel" until a reload. Same fix as every other
// cross-component sync in this app (nextep:progress-marked, nextep:plan-added, …): a
// window event the hook below listens for and merges in.
function broadcastRating(showId, season, episode, rating) {
  try { window.dispatchEvent(new CustomEvent('nextep:episode-rated', { detail: { showId: Number(showId), season, episode, rating } })) } catch {}
}

export function useEpisodeRatings(showId, userId) {
  const [ratings, setRatings] = useState({})

  useEffect(() => {
    if (!userId || !showId) { setRatings({}); return }
    let cancel = false
    supabase.from('episode_ratings')
      .select('season, episode, rating')
      .eq('user_id', userId).eq('show_id', Number(showId))
      .then(({ data }) => {
        if (cancel || !data) return
        const m = {}
        data.forEach(r => { m[`${r.season}-${r.episode}`] = Number(r.rating) })
        setRatings(m)
      }, () => {})
    return () => { cancel = true }
  }, [showId, userId])

  // Pick up a rating saved through the OTHER write path (see broadcastRating above) —
  // the deck's hold-to-rate and Episode Pulse's post-mark peek both rate episodes of
  // this same show without ever mounting this hook.
  useEffect(() => {
    if (!showId) return
    const onRated = (e) => {
      const d = e.detail
      if (!d || Number(d.showId) !== Number(showId)) return
      const key = `${d.season}-${d.episode}`
      setRatings(prev => {
        if (d.rating == null) { if (!(key in prev)) return prev; const n = { ...prev }; delete n[key]; return n }
        if (prev[key] === d.rating) return prev
        return { ...prev, [key]: d.rating }
      })
    }
    window.addEventListener('nextep:episode-rated', onRated)
    return () => window.removeEventListener('nextep:episode-rated', onRated)
  }, [showId])

  const rate = useCallback(async (season, episode, rating) => {
    if (!userId || !showId) return
    const key = `${season}-${episode}`
    // Tapping the current value again clears it (mirrors ReelRating's clear).
    const clearing = rating == null || rating === 0
    setRatings(prev => {
      const next = { ...prev }
      if (clearing) delete next[key]; else next[key] = rating
      return next
    })
    if (clearing) {
      const { error } = await labsWrite('clear episode rating', () => supabase.from('episode_ratings').delete()
        .match({ user_id: userId, show_id: Number(showId), season, episode }))
      if (error) { console.error('[ep rating delete]', error.message); window.dispatchEvent(new CustomEvent('nextep:save-failed', { detail: { action: 'clear episode rating' } })) }
      return
    }
    const { error } = await labsWrite('save episode rating', () => supabase.from('episode_ratings').upsert(
      { user_id: userId, show_id: Number(showId), season, episode, rating },
      { onConflict: 'user_id,show_id,season,episode' }
    ))
    if (error) { console.error('[ep rating save]', error.message); window.dispatchEvent(new CustomEvent('nextep:save-failed', { detail: { action: 'save episode rating' } })) }
  }, [showId, userId])

  const values = Object.values(ratings)
  const avg = values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10 : null
  return { ratings, rate, avg, count: values.length }
}
